import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  Navigate,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { AuthProvider } from "@/hooks/useAuth";
import { Toaster } from "sonner";
import { CookieConsentBanner } from "@/components/legal/CookieConsentBanner";
import { organizationSchema, websiteSchema } from "@/lib/seo-schemas";
import { AnalyticsListener } from "@/components/AnalyticsListener";
import { NotFoundPage } from "@/components/errors/NotFoundPage";
import { ServerErrorPage } from "@/components/errors/ServerErrorPage";
import { PageLoadingBar } from "@/components/loading/PageLoadingBar";

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <ServerErrorPage
      error={error}
      resetError={() => {
        router.invalidate();
        reset();
      }}
    />
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Currency Thoughts — Building Generational Wealth" },
      {
        name: "description",
        content:
          "Currency Thoughts LLC is a veteran-led, family-focused independent insurance agency building the digital infrastructure modern Advisors trust.",
      },
      { name: "author", content: "Currency Thoughts LLC" },
      { property: "og:site_name", content: "Currency Thoughts" },
      { property: "og:title", content: "Currency Thoughts — Building Generational Wealth" },
      {
        property: "og:description",
        content:
          "A veteran-led, family-focused independent insurance agency. Calm, generational, education-first.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Currency Thoughts — Building Generational Wealth" },
      { name: "twitter:description", content: "Family Wealth Strategists. Economic Empowerment Coaches. Building generational wealth for the families we serve." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/bHmSqbt1WPTLt9hQLuR0lzVDH912/social-images/social-1779309924121-26CEB8FE-487E-4ADE-8B88-327CD2B41C55.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/bHmSqbt1WPTLt9hQLuR0lzVDH912/social-images/social-1779309924121-26CEB8FE-487E-4ADE-8B88-327CD2B41C55.webp" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Allura&family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600;700&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(organizationSchema()),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteSchema()),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const maintenance =
    (import.meta.env.VITE_MAINTENANCE_MODE as string | undefined) === "true";
  // Maintenance gate: redirect everything except /admin* and /maintenance itself
  // so admins can still log in and disable the flag.
  const shouldRedirectToMaintenance =
    maintenance &&
    !pathname.startsWith("/admin") &&
    !pathname.startsWith("/login") &&
    pathname !== "/maintenance";

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <PageLoadingBar />
        {shouldRedirectToMaintenance ? <Navigate to="/maintenance" /> : <Outlet />}
        <AnalyticsListener />
        <Toaster richColors position="top-right" />
        <CookieConsentBanner />
      </AuthProvider>
    </QueryClientProvider>
  );
}
